/**
 * Таймлайны агроциклов: лента сезона (две дорожки на экране «Агроциклы») и
 * хронология работ (главный блок детали цикла). Чистый SVG/CSS без библиотек.
 *
 * Компонент локальный: он знает про агроцикл, а примитивы в `src/ui` — нет.
 * Всё, что тут рисуется, дублируется словами: подпись, title и текстовая
 * колонка справа. В оттенках серого смысл не теряется.
 */

import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';
import {
  countOf,
  dateShort,
  ha,
  num,
  pctAxis,
  sentence,
  share,
  windowLabel,
  WORDS,
} from '../lib/format';
import type { CycleStatus, Work } from '../data/types';
import './cycles.css';

/* ----------------------------------------------------------------- шкала */

const m = defineMessages({
  monthTick: {
    id: 'cycle.timeline.monthTick',
    defaultMessage:
      '{month, select, 1 {янв} 2 {фев} 3 {мар} 4 {апр} 5 {май} 6 {июн} 7 {июл} 8 {авг} 9 {сен} 10 {окт} 11 {ноя} 12 {дек} other {}}',
    description:
      'Короткое имя месяца на оси времени (подпись тика), стоит само по себе, без числа: по-русски именительный падеж («май»); {month} — номер 1–12; 3–4 буквы',
  },
  days: {
    id: 'cycle.timeline.days',
    defaultMessage: '{count, plural, one {# день} few {# дня} many {# дней} other {# дня}}',
    description: 'Срок в днях: «4 дня», «15 дней»; {count} — число',
  },
  microReportedArea: {
    id: 'cycle.timeline.micro.area',
    defaultMessage: 'отчитались {reported}, без отчёта {silent} из {total} га работ',
    description: 'aria-label полосы долей выполнения (хронология работ), счёт в гектарах работ; {reported}/{silent}/{total} — числа строкой',
  },
  microReportedCount: {
    id: 'cycle.timeline.micro.count',
    defaultMessage: 'отчитались {reported}, без отчёта {silent} из {total, plural, one {# работы на поле} other {# работ на полях}}',
    description: 'aria-label полосы долей выполнения (хронология работ), счёт в работах на полях; {reported}/{silent} — числа строкой, {total} — число',
  },
  season: { id: 'cycle.timeline.season', defaultMessage: 'Сезон', description: 'Подпись левой колонки оси ленты сезона (заголовок строки с месяцами)' },
  today: { id: 'cycle.timeline.today', defaultMessage: 'сегодня', description: 'Подпись метки даты среза на оси времени; коротко' },
  ribbonLane: {
    id: 'cycle.timeline.ribbon.lane',
    defaultMessage: '{title}: {window}, {stages, plural, one {# этап} few {# этапа} many {# этапов} other {# этапа}}. Открыть агроцикл',
    description: 'aria-label полосы агроцикла в ленте сезона (ссылка); {title} — имя агроцикла, {window} — окно дат, {stages} — число этапов',
  },
  ribbonSegWorks: {
    id: 'cycle.timeline.ribbon.seg',
    defaultMessage: '{name}. {window}. {works}',
    description: 'Подсказка отрезка этапа в ленте сезона: {name} — название этапа, {window} — окно дат, {works} — «5 работ» или фраза про отсутствие работ',
  },
  ribbonSegClipped: {
    id: 'cycle.timeline.ribbon.segClipped',
    defaultMessage: '{name}. {window}. {works}. Часть окна этапа выходит за границу агроцикла — отрезок обрезан по полосе',
    description: 'Подсказка отрезка этапа в ленте сезона, когда окно этапа шире агроцикла; аргументы как у обычной подсказки',
  },
  noPlanWorks: { id: 'cycle.timeline.ribbon.noWorks', defaultMessage: 'Плановых работ нет', description: 'Фрагмент подсказки этапа в ленте сезона: в этапе нет работ плана' },
  statusDone: { id: 'cycle.timeline.status.done', defaultMessage: 'окно закрыто', description: 'Состояние работы плана: окно выполнения закрыто (работа позади); подпись и пункт легенды' },
  statusActive: { id: 'cycle.timeline.status.active', defaultMessage: 'окно идёт', description: 'Состояние работы плана: окно выполнения открыто сейчас; подпись и пункт легенды' },
  statusUpcoming: { id: 'cycle.timeline.status.upcoming', defaultMessage: 'впереди', description: 'Состояние работы плана: окно ещё впереди; подпись и пункт легенды' },
  tailAhead: { id: 'cycle.timeline.tail.ahead', defaultMessage: 'впереди', description: 'Правая колонка хронологии работ, верхняя строка: окно ещё впереди' },
  tailStartsIn: { id: 'cycle.timeline.tail.startsIn', defaultMessage: 'старт через {days}', description: 'Правая колонка хронологии, нижняя строка: до старта окна; {days} — «4 дня»' },
  tailNotOpen: { id: 'cycle.timeline.tail.notOpen', defaultMessage: 'окно ещё не открыто', description: 'Правая колонка хронологии, нижняя строка: дата старта окна неизвестна' },
  tailAxes: { id: 'cycle.timeline.tail.axes', defaultMessage: 'отч. {pct}', description: 'Правая колонка хронологии: доля отчитавшихся по числу работ на полях; {pct} — «78 %»; коротко' },
  tailAxesArea: { id: 'cycle.timeline.tail.axesArea', defaultMessage: 'отч. {pct} (га работ)', description: 'Правая колонка хронологии: доля отчитавшихся по площади работ; {pct} — «78 %»; коротко' },
  tailClosesIn: { id: 'cycle.timeline.tail.closesIn', defaultMessage: 'до закрытия {days}', description: 'Правая колонка хронологии, верхняя строка идущего окна: сколько осталось; {days} — «4 дня»' },
  tailActive: { id: 'cycle.timeline.tail.active', defaultMessage: 'окно идёт', description: 'Правая колонка хронологии, верхняя строка: окно идёт, срок закрытия неизвестен' },
  tailOverdue: { id: 'cycle.timeline.tail.overdue', defaultMessage: 'после срока {ha} га работ', description: 'Правая колонка хронологии, закрытое окно: площадь работ без отчёта после срока; {ha} — число строкой' },
  tailSilent: { id: 'cycle.timeline.tail.silent', defaultMessage: 'без отчёта {count}', description: 'Правая колонка хронологии, закрытое окно: сколько работ на полях без отчёта; {count} — число строкой' },
  axisWork: { id: 'cycle.timeline.axis.work', defaultMessage: 'Работа и окно', description: 'Заголовок левой колонки хронологии работ' },
  axisTail: { id: 'cycle.timeline.axis.tail', defaultMessage: 'Две оси', description: 'Заголовок правой колонки хронологии работ: две оси — доля по площади и по числу работ' },
  conditionTitle: { id: 'cycle.timeline.condition.title', defaultMessage: 'Условие: {condition}', description: 'Подсказка у пометки «условная»: текст условия из шаблона' },
  conditional: { id: 'cycle.timeline.condition.label', defaultMessage: 'условная', description: 'Пометка у работы плана: работа назначается только при условии; коротко' },
  barArea: {
    id: 'cycle.timeline.bar.area',
    defaultMessage: '{name}. {status}, {window}. Работ на полях {count}; га работ {total}, отчитались {reported}.',
    description: 'Подсказка полосы работы в хронологии, когда есть площадь работ; {status} — «Окно идёт», {window} — окно дат, остальное — числа строкой',
  },
  barCount: {
    id: 'cycle.timeline.bar.count',
    defaultMessage: '{name}. {status}, {window}. Работ на полях {count}, отчитались {reported}, без отчёта {silent}.',
    description: 'Подсказка полосы работы в хронологии, когда площади работ нет; {status} — «Окно идёт», остальное — числа строкой',
  },
  offScale: { id: 'cycle.timeline.offScale', defaultMessage: 'окно вне оси агроцикла', description: 'Вместо полосы в хронологии: окно работы лежит за пределами оси времени' },
  closing: {
    id: 'cycle.timeline.legend.closing',
    defaultMessage: 'закрывается ≤{count, plural, one {# дня} other {# дней}} — срок, а не нарушение',
    description: 'Пункт легенды хронологии: окно, которое скоро закроется; {count} — число дней порога',
  },
  legendSplit: { id: 'cycle.timeline.legend.split', defaultMessage: 'доля отчитавшихся внутри полосы, остаток — без отчёта', description: 'Пункт легенды хронологии: что значит заливка внутри полосы' },
  legendToday: { id: 'cycle.timeline.legend.today', defaultMessage: 'дата среза', description: 'Пункт легенды хронологии: вертикальная метка даты среза данных' },
  legendOffAxis: { id: 'cycle.timeline.legend.offAxis', defaultMessage: 'Дата среза {date} лежит за краем оси агроцикла — маркера на шкале нет.', description: 'Легенда хронологии: дата среза не попала на ось; {date} — дата' },
  legendNoDate: { id: 'cycle.timeline.legend.noDate', defaultMessage: 'Даты среза в данных нет — маркер «сегодня» не поставлен.', description: 'Легенда хронологии: у данных нет даты среза' },
});

const DAY_MS = 86_400_000;

/** ISO-дата в миллисекунды UTC; всё нераспознанное — null, а не 0. */
export function toTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const parts = iso.slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number) as [number, number, number];
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return Date.UTC(y, m - 1, d);
}

/** Целых суток от `a` до `b`; отрицательное — дата уже позади. */
export function daysBetween(a: string | null | undefined, b: string | null | undefined): number | null {
  const from = toTime(a);
  const to = toTime(b);
  if (from === null || to === null) return null;
  return Math.round((to - from) / DAY_MS);
}

/** «4 дня», «15 дней» — число и слово по правилам языка. */
export function daysLabel(count: number): string {
  return tr(m.days, { count });
}

export interface TimeTick {
  key: string;
  label: string;
  /** Позиция в процентах ширины дорожки. */
  x: number;
  /** Первый месяц года — под ним печатается год. */
  yearLabel: string | null;
}

export interface Scale {
  fromIso: string;
  toIso: string;
  /** Позиция даты в процентах; вне диапазона — null (дата не на этой оси). */
  at(iso: string | null | undefined): number | null;
  /** Позиция с прижатием к краям: для отрезков, торчащих за диапазон. */
  clamp(iso: string | null | undefined): number | null;
  ticks: TimeTick[];
}

function isoOf(time: number): string {
  const d = new Date(time);
  const m = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${d.getUTCDate()}`.padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}-${day}`;
}

/** Начало месяца даты — чтобы ось начиналась с ровного тика. */
export function monthFloor(iso: string): string {
  const t = toTime(iso);
  if (t === null) return iso;
  const d = new Date(t);
  return isoOf(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

/** Начало следующего месяца — правый край оси. */
export function monthCeil(iso: string): string {
  const t = toTime(iso);
  if (t === null) return iso;
  const d = new Date(t);
  return isoOf(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
}

/** Дата, сдвинутая на целое число лет; 29 февраля садится на 28-е. */
export function shiftYears(iso: string, years: number): string {
  const t = toTime(iso);
  if (t === null || years === 0) return iso;
  const d = new Date(t);
  const year = d.getUTCFullYear() + years;
  const month = d.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return isoOf(Date.UTC(year, month, Math.min(d.getUTCDate(), lastDay)));
}

/**
 * На сколько лет сдвинуть окна шаблона, чтобы они легли в год своего цикла.
 *
 * Шаблон описывает один канонический сезон, а исторический цикл ссылается на
 * тот же `template_id`: без сдвига его стадии рисуются на дорожке чужого года.
 * Сдвиг подбирается по наибольшему перекрытию с окном цикла — это устойчивее
 * вычитания годов, когда сезон переходит через январь (озимая пшеница).
 */
export function bestYearShift(
  planFrom: string,
  planTo: string,
  windowFrom: string,
  windowTo: string,
): number {
  const cycleFrom = toTime(windowFrom);
  const cycleTo = toTime(windowTo);
  if (cycleFrom === null || cycleTo === null) return 0;

  let best = 0;
  let bestOverlap = -Infinity;
  for (let shift = -6; shift <= 6; shift += 1) {
    const from = toTime(shiftYears(planFrom, shift));
    const to = toTime(shiftYears(planTo, shift));
    if (from === null || to === null) continue;
    const overlap = Math.min(to, cycleTo) - Math.max(from, cycleFrom);
    if (overlap > bestOverlap || (overlap === bestOverlap && Math.abs(shift) < Math.abs(best))) {
      bestOverlap = overlap;
      best = shift;
    }
  }
  return best;
}

/**
 * Пересечение окна с границами полосы. ISO-строки сравниваются лексикографически
 * и это же хронологический порядок. `null` — окно не задевает полосу вовсе.
 */
export function clipWindow(
  from: string,
  to: string,
  bounds: { from: string; to: string },
): { from: string; to: string; clipped: boolean } | null {
  const a = from < bounds.from ? bounds.from : from;
  const b = to > bounds.to ? bounds.to : to;
  if (a > b) return null;
  return { from: a, to: b, clipped: a !== from || b !== to };
}

export function makeScale(fromIso: string, toIso: string): Scale | null {
  const from = toTime(fromIso);
  const to = toTime(toIso);
  if (from === null || to === null || to <= from) return null;
  const span = to - from;

  const raw = (iso: string | null | undefined): number | null => {
    const t = toTime(iso);
    if (t === null) return null;
    return ((t - from) / span) * 100;
  };

  const ticks: TimeTick[] = [];
  const start = new Date(from);
  let year = start.getUTCFullYear();
  let month = start.getUTCMonth();
  // Первый тик — начало месяца, в котором лежит `from`; тики левее оси отсекаются.
  for (let guard = 0; guard < 64; guard += 1) {
    const t = Date.UTC(year, month, 1);
    if (t > to) break;
    const x = ((t - from) / span) * 100;
    if (x >= -0.01) {
      ticks.push({
        key: `${year}-${month}`,
        label: tr(m.monthTick, { month: String(month + 1) }),
        x,
        yearLabel: month === 0 || ticks.length === 0 ? `${year}` : null,
      });
    }
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }

  return {
    fromIso,
    toIso,
    at: (iso) => {
      const x = raw(iso);
      if (x === null || x < 0 || x > 100) return null;
      return x;
    },
    clamp: (iso) => {
      const x = raw(iso);
      if (x === null) return null;
      return Math.min(100, Math.max(0, x));
    },
    ticks,
  };
}

/**
 * Подпись у самого края оси прижимается внутрь: тик на 100 % с обычным
 * `translateX(-50%)` наполовину уезжает под обрез горизонтальной прокрутки,
 * и последний месяц читается как «де».
 */
function tickShift(x: number): string {
  if (x <= 2) return 'translateX(0)';
  if (x >= 98) return 'translateX(-100%)';
  return 'translateX(-50%)';
}

/** Геометрия отрезка окна на дорожке; null — окно вне диапазона оси. */
function spanStyle(scale: Scale, from: string, to: string, minPct = 0.8): CSSProperties | null {
  const a = scale.clamp(from);
  const b = scale.clamp(to);
  if (a === null || b === null) return null;
  const left = Math.min(a, b);
  const width = Math.max(minPct, Math.abs(b - a));
  return { left: `${left}%`, width: `${Math.min(100 - left, width)}%` };
}

/* --------------------------------------------------------- микро-DoneSplit */

/**
 * Две оси в масштабе полосы Ганта. Одну ось показать нельзя: обе строки
 * рисуются всегда, а остаток назван словом «без отчёта» в подписи рядом.
 * Полноразмерный `DoneSplit` живёт в таблице ниже — здесь он не помещается.
 */
export function MicroSplit({
  total,
  reported,
  basis = 'count',
}: {
  total: number;
  reported: number;
  /** Знаменатель полосы: `area` — гектары работ, `count` — работы на полях. */
  basis?: 'area' | 'count';
}) {
  const t = useT();
  const r = share(reported, total);
  const silent = Math.max(0, total - reported);
  const label =
    basis === 'area'
      ? t(m.microReportedArea, { reported: num(reported), silent: num(silent), total: num(total) })
      : t(m.microReportedCount, { reported: num(reported), silent: num(silent), total });

  return (
    <span className="micro" role="img" aria-label={label}>
      <span className="micro__row">
        <span
          className="micro__fill micro__fill--reported"
          style={{ width: `${Math.min(100, r ?? 0)}%` }}
        />
      </span>
    </span>
  );
}

/* ------------------------------------------------------------ лента сезона */

export interface RibbonSegment {
  id: string;
  name: string;
  from: string;
  to: string;
  /** Сколько работ приходится на стадию — уходит в подсказку. */
  works: number;
}

export interface RibbonTrack {
  id: string;
  /** Имя единицы: по нему происходит спуск на уровень цикла. */
  title: string;
  note: string;
  to: string;
  window: { from: string; to: string };
  segments: RibbonSegment[];
  /**
   * Состояние цикла. `planned` приходит только из канона — опубликованный
   * шаблон без запущенных кампаний; своя полоса нужна, чтобы «срока ещё нет»
   * не рисовалось как «срок прошёл».
   */
  status: CycleStatus;
}

/**
 * Стадии в шаблоне пересекаются (весенняя вегетация начинается до конца
 * возобновления). Наложенные отрезки прятали бы друг друга, поэтому дорожка
 * раскладывается на полосы: отрезок садится в первую полосу, где он никого
 * не задевает. Пересечение — факт данных, и его видно, а не заметено.
 */
function packSegments(segments: RibbonSegment[]): RibbonSegment[][] {
  const sorted = [...segments].sort((a, b) => a.from.localeCompare(b.from));
  const bands: { end: string; items: RibbonSegment[] }[] = [];

  for (const seg of sorted) {
    const band = bands.find((b) => b.end <= seg.from);
    if (band) {
      band.items.push(seg);
      if (seg.to > band.end) band.end = seg.to;
    } else {
      bands.push({ end: seg.to, items: [seg] });
    }
  }

  return bands.map((b) => b.items);
}

export function SeasonRibbon({
  tracks,
  scale,
  asOf,
}: {
  tracks: RibbonTrack[];
  scale: Scale;
  asOf: string | null;
}) {
  const t = useT();
  const todayX = asOf ? scale.at(asOf) : null;

  return (
    <div className="ribbon">
      <div className="ribbon__row ribbon__row--axis">
        <div className="ribbon__side ribbon__side--axis">{t(m.season)}</div>
        <div className="ribbon__lane ribbon__lane--axis">
          {scale.ticks.map((tick) => (
            <span
              className="ribbon__tick"
              key={tick.key}
              style={{ left: `${tick.x}%`, transform: tickShift(tick.x) }}
            >
              <span className="ribbon__tickname">{tick.label}</span>
              {tick.yearLabel ? <span className="ribbon__tickyear">{tick.yearLabel}</span> : null}
            </span>
          ))}
          {todayX === null ? null : (
            <span
              className="ribbon__todaytag"
              style={{ left: `${todayX}%`, transform: tickShift(todayX) }}
            >
              {t(m.today)}
            </span>
          )}
        </div>
      </div>

      {tracks.map((track) => (
        <div className="ribbon__row" key={track.id}>
          <div className="ribbon__side">
            <Link className="ribbon__name" to={track.to}>
              {track.title}
            </Link>
            <span className="ribbon__note">{track.note}</span>
          </div>

          <Link
            className={`ribbon__lane ribbon__lane--${track.status}`}
            to={track.to}
            aria-label={t(m.ribbonLane, {
              title: track.title,
              window: windowLabel(track.window.from, track.window.to),
              stages: track.segments.length,
            })}
          >
            <span className="ribbon__bands">
              {packSegments(track.segments).map((band, bandIndex) => (
                <span className="ribbon__band" key={`${track.id}-band-${bandIndex}`}>
                  {band.map((seg) => {
                    // Геометрия зажимается окном своего цикла: стадия шаблона
                    // может торчать за границу полосы, и тогда отрезок читался
                    // бы как чужой. Подпись при этом остаётся честной — она
                    // показывает окно стадии целиком, а обрезка названа словами.
                    const box = clipWindow(seg.from, seg.to, track.window);
                    if (!box) return null;
                    const style = spanStyle(scale, box.from, box.to);
                    if (!style) return null;
                    return (
                      <span
                        className={`ribbon__seg ribbon__seg--${track.status}`}
                        key={seg.id}
                        style={style}
                        title={t(box.clipped ? m.ribbonSegClipped : m.ribbonSegWorks, {
                          name: seg.name,
                          window: windowLabel(seg.from, seg.to),
                          works: seg.works > 0 ? countOf(seg.works, WORDS.work) : t(m.noPlanWorks),
                        })}
                      >
                        <span className="ribbon__segname">{seg.name}</span>
                        <span className="ribbon__segwin">{windowLabel(seg.from, seg.to)}</span>
                      </span>
                    );
                  })}
                </span>
              ))}
            </span>

            {(() => {
              const base = spanStyle(scale, track.window.from, track.window.to);
              return base ? <span className="ribbon__base" style={base} /> : null;
            })()}

            {todayX === null ? null : (
              <span className="ribbon__today" style={{ left: `${todayX}%` }} aria-hidden="true" />
            )}
          </Link>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------- хронология работ */

/** Подпись состояния работы: «окно закрыто», «окно идёт», «впереди». */
export function workStatusLabel(status: Work['status']): string {
  return tr(status === 'done' ? m.statusDone : status === 'active' ? m.statusActive : m.statusUpcoming);
}

/** Порог «окно закрывается» — тот же, что у зоны внимания window_closing. */
export const CLOSING_DAYS = 5;

/**
 * «Закрывается» — не отдельный статус в данных, а производная от даты среза:
 * идущее окно, до конца которого осталось ≤5 дней. Отдельным значением в
 * `Work.status` это не становится — иначе пришлось бы хранить в фикстуре
 * величину, которая меняется от того, какой день считать сегодняшним.
 * Возвращает пустую строку, чтобы имя класса склеивалось без ветвлений.
 */
export function closingModifier(work: Work, asOf: string | null): string {
  if (work.status !== 'active' || !asOf) return '';
  const left = daysBetween(asOf, work.window.to);
  if (left === null || left < 0 || left > CLOSING_DAYS) return '';
  return ' chrono__bar--closing';
}

/** Условность работы объявлена шаблоном и доехала в `note` каскадом. */
export function conditionOf(work: Work): string | null {
  if (!work.note) return null;
  // i18n-ignore: маркер ищется в тексте примечания из данных, а не показывается на экране
  const marker = 'условная работа';
  const at = work.note.indexOf(marker);
  if (at < 0) return null;
  const tail = work.note.slice(at + marker.length).replace(/^\s*[:—-]\s*/, '');
  return tail.length > 0 ? tail : work.note;
}

export interface StageGroup {
  stage: string;
  works: Work[];
  from: string;
  to: string;
}

export function groupByStage(works: Work[]): StageGroup[] {
  const order: string[] = [];
  const map = new Map<string, Work[]>();
  for (const work of works) {
    let bucket = map.get(work.stage);
    if (!bucket) {
      bucket = [];
      map.set(work.stage, bucket);
      order.push(work.stage);
    }
    bucket.push(work);
  }
  return order.map((stage) => {
    const bucket = map.get(stage) ?? [];
    const from = bucket.reduce((min, w) => (w.window.from < min ? w.window.from : min), bucket[0]!.window.from);
    const to = bucket.reduce((max, w) => (w.window.to > max ? w.window.to : max), bucket[0]!.window.to);
    return { stage, works: bucket, from, to };
  });
}

function tailText(work: Work, asOf: string | null): ReactNode {
  if (work.status === 'upcoming') {
    const left = asOf ? daysBetween(asOf, work.window.from) : null;
    return (
      <span className="chrono__tail">
        <span className="chrono__tailtop">{tr(m.tailAhead)}</span>
        <span className="chrono__tailsub">
          {left !== null && left > 0 ? tr(m.tailStartsIn, { days: daysLabel(left) }) : tr(m.tailNotOpen)}
        </span>
      </span>
    );
  }

  // Доля — по гектарам работ, когда сервер их назвал (design §7.3): счёт
  // обязательств не равен земле. Нет гектаров (фикстуры) — прежний счёт.
  const area = work.totals.area;
  const reported = area
    ? share(area.reported, area.total)
    : share(work.totals.reported, work.totals.obligations);
  const axes = tr(area ? m.tailAxesArea : m.tailAxes, { pct: pctAxis(reported) });

  if (work.status === 'active') {
    const left = asOf ? daysBetween(asOf, work.window.to) : null;
    return (
      <span className="chrono__tail">
        <span className="chrono__tailtop">
          {left !== null && left >= 0 ? tr(m.tailClosesIn, { days: daysLabel(left) }) : tr(m.tailActive)}
        </span>
        <span className="chrono__tailsub">{axes}</span>
      </span>
    );
  }

  return (
    <span className="chrono__tail">
      <span className="chrono__tailtop">{axes}</span>
      <span className="chrono__tailsub">
        {area
          ? area.overdue !== null
            ? tr(m.tailOverdue, { ha: ha(area.overdue) })
            : countOf(work.totals.obligations, WORDS.obligation)
          : tr(m.tailSilent, { count: num(work.totals.silent) })}
      </span>
    </span>
  );
}

export function WorksChronology({
  works,
  scale,
  asOf,
  hrefFor,
  stages,
  markConditional = true,
}: {
  works: Work[];
  scale: Scale;
  asOf: string | null;
  hrefFor: (work: Work) => string;
  /**
   * Какие стадии показывать строками. Шкала при этом остаётся шкалой всего
   * цикла: свёрнутые стадии не сдвигают ось, иначе показанные окна поехали бы.
   */
  stages?: string[];
  /**
   * Пометка «условная» у строки. Когда условны все работы цикла, пометка
   * ничего не различает и говорится один раз в легенде.
   */
  markConditional?: boolean;
}) {
  const t = useT();
  const all = groupByStage(works);
  const groups = stages ? all.filter((g) => stages.includes(g.stage)) : all;
  const todayX = asOf ? scale.at(asOf) : null;

  return (
    <div className="chrono">
      <div className="chrono__row chrono__row--axis">
        <div className="chrono__side chrono__side--axis">{t(m.axisWork)}</div>
        <div className="chrono__lane chrono__lane--axis">
          {scale.ticks.map((tick) => (
            <span
              className="chrono__tick"
              key={tick.key}
              style={{ left: `${tick.x}%`, transform: tickShift(tick.x) }}
            >
              {tick.label}
            </span>
          ))}
          {todayX === null ? null : (
            <span
              className="chrono__todaytag"
              style={{ left: `${todayX}%`, transform: tickShift(todayX) }}
            >
              {t(m.today)}
            </span>
          )}
        </div>
        {/* Заголовок несёт тот же модификатор, что и ячейки: иначе при
            прокрутке колонка примораживается, а её шапка уезжает. */}
        <div className="chrono__side chrono__side--axis chrono__side--tail">{t(m.axisTail)}</div>
      </div>

      {groups.map((group) => (
        <Fragment key={group.stage}>
          <div className="chrono__stage">
            <span className="chrono__stagename">{group.stage}</span>
            <span className="chrono__stagenote">
              {countOf(group.works.length, WORDS.work)} · {windowLabel(group.from, group.to)}
            </span>
          </div>

          {group.works.map((work) => {
            const style = spanStyle(scale, work.window.from, work.window.to, 1.2);
            const condition = conditionOf(work);
            return (
              <div className="chrono__row" key={work.id}>
                <div className="chrono__side">
                  <Link className="chrono__name" to={hrefFor(work)}>
                    {work.name}
                  </Link>
                  <span className="chrono__meta">
                    {windowLabel(work.window.from, work.window.to)}
                    {condition && markConditional ? (
                      <>
                        {' · '}
                        <span className="chrono__cond" title={t(m.conditionTitle, { condition })}>
                          {t(m.conditional)}
                        </span>
                      </>
                    ) : null}
                  </span>
                </div>

                <div className="chrono__lane">
                  {style ? (
                    <span
                      className={`chrono__bar chrono__bar--${work.status}${closingModifier(
                        work,
                        asOf,
                      )}`}
                      style={style}
                      title={
                        work.totals.area
                          ? t(m.barArea, {
                              name: work.name,
                              status: sentence(workStatusLabel(work.status)),
                              window: windowLabel(work.window.from, work.window.to),
                              count: num(work.totals.obligations),
                              total: num(work.totals.area.total),
                              reported: num(work.totals.area.reported),
                            })
                          : t(m.barCount, {
                              name: work.name,
                              status: sentence(workStatusLabel(work.status)),
                              window: windowLabel(work.window.from, work.window.to),
                              count: num(work.totals.obligations),
                              reported: num(work.totals.reported),
                              silent: num(work.totals.silent),
                            })
                      }
                    >
                      {work.status === 'upcoming' ? null : (
                        <MicroSplit
                          total={work.totals.area ? work.totals.area.total : work.totals.obligations}
                          reported={work.totals.area ? work.totals.area.reported : work.totals.reported}
                          basis={work.totals.area ? 'area' : 'count'}
                        />
                      )}
                    </span>
                  ) : (
                    <span className="chrono__offscale">{t(m.offScale)}</span>
                  )}

                  {todayX === null ? null : (
                    <span className="chrono__today" style={{ left: `${todayX}%` }} aria-hidden="true" />
                  )}
                </div>

                <div className="chrono__side chrono__side--tail">{tailText(work, asOf)}</div>
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Легенда хронологии: смысл полос читается подписью, а не только цветом.
 * Маркера даты среза в легенде нет, когда его нет и на оси: у закрытого цикла
 * срез лежит за правым краем — и это говорится словами, а не молчанием.
 */
export function ChronoLegend({
  note,
  todayOnScale,
  asOf,
  closing = 0,
}: {
  note?: string;
  todayOnScale: boolean;
  asOf: string | null;
  /** Сколько идущих окон закрывается ≤5 дней: позиция легенды нужна только
   *  тогда, когда такая полоса на шкале действительно есть. */
  closing?: number;
}) {
  const t = useT();
  return (
    <div className="chrono__legend">
      <span className="chrono__key">
        <span className="chrono__swatch chrono__swatch--done" />
        {t(m.statusDone)}
      </span>
      <span className="chrono__key">
        <span className="chrono__swatch chrono__swatch--active" />
        {t(m.statusActive)}
      </span>
      {closing > 0 ? (
        <span className="chrono__key">
          <span className="chrono__swatch chrono__swatch--closing" />
          {t(m.closing, { count: CLOSING_DAYS })}
        </span>
      ) : null}
      <span className="chrono__key">
        <span className="chrono__swatch chrono__swatch--upcoming" />
        {t(m.statusUpcoming)}
      </span>
      <span className="chrono__key">
        <span className="chrono__swatch chrono__swatch--split" />
        {t(m.legendSplit)}
      </span>
      {todayOnScale ? (
        <span className="chrono__key">
          <span className="chrono__swatch chrono__swatch--today" />
          {t(m.legendToday)}
        </span>
      ) : (
        <span className="chrono__key">
          {asOf ? t(m.legendOffAxis, { date: dateShort(asOf) }) : t(m.legendNoDate)}
        </span>
      )}
      {note ? <span className="chrono__keynote">{note}</span> : null}
    </div>
  );
}
